
import {
  Address,
  Coordinates,
  ClientPageConfigPickupPoint,
  OpeningHoursInterval,
  OpeningHours,
} from '@/types'
import Vue from 'vue'

const daysOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
export default Vue.extend({
  name: 'pickup-point-body-viewer',
  props: {
    openingHours: {
      type: Object as () => OpeningHours,
      required: true,
    },
    address: {
      type: Object as () => Address,
      required: true,
    },
    coordinates: {
      type: Object as () => Coordinates,
      required: true,
    },
    closedExceptionalDays: {
      type: Array,
      default: () => [],
    },
    config: {
      type: Object as () => ClientPageConfigPickupPoint,
      required: true,
    },
    userLanguage: {
      type: String,
      default: 'en-US',
    },
  },
  computed: {
    cssColor() {
      return {
        '--body-viewer-primary-color': this.config.theme.primaryColor || '#000000',
      }
    },
    primaryColor() {
      return this.config.theme.primaryColor || '#000000'
    },
    convertedOpeningHours(): OpeningHours {
      const todayIndex = new Date().getDay()
      const rotatedDaysOfWeek = daysOfWeek.slice(todayIndex).concat(daysOfWeek.slice(0, todayIndex)) as Array<keyof OpeningHours>
      return rotatedDaysOfWeek.reduce((result, day) => {
        result[day] = this.openingHours[day]
        return result
      }, {} as OpeningHours)
    },
    blocContactTitle(): string {
      return this.config?.contact?.title
        ? this.config?.contact.title
        : this.$t('pickupPoint.contactTitle')
    },
    email(): string {
      if (this.showContactEmail) {
        return this.config.contact.email.value as string
      }
      return ''
    },
    showContactEmail(): boolean {
      return (this.config.contact.email?.type &&
        this.config.contact.email.type.toLowerCase() !== 'empty' &&
        this.config.contact.email.value) as boolean
    },
    phone(): string {
      if (this.showContactPhone) {
        return this.config.contact.phone.number as string
      }
      return ''
    },
    showContactPhone(): boolean {
      return (this.config.contact.phone?.type &&
        this.config.contact.phone.type.toLowerCase() !== 'empty' &&
        this.config.contact.phone.number) as boolean
    },
    showPickupPointClosingDates(): boolean {
      if (this.config.details && this.closedExceptionalDays.length) {
        return this.config.details.showPickupPointClosingDates as boolean
      }
      return !!this.closedExceptionalDays.length
    },
    showPickupPointOpeningHours(): boolean {
      if (this.config.details) {
        return this.config.details.showPickupPointOpeningHours as boolean
      }
      return !!this.openingHours
    },
    showPickupPointAddressAdditional(): boolean {
      if (this.config.details) {
        return this.config.details.showPickupPointAddressAdditional as boolean
      }
      return !!this.addressLine2
    },
    showPickupPointInfosAdditional(): boolean {
        return this.config.details?.showPickupPointInfosAdditional as boolean
    },
    showPickupPointAddress(): boolean {
      if (this.config.details) {
        return this.config.details.showPickupPointAddress as boolean
      }
      return !!(
        this.address ||
        this.addressLine1 ||
        this.addressLine2 ||
        this.city ||
        this.postalCode ||
        this.country ||
        this.addressTitle
      )
    },
    blocAddressTitle(): string {
      return this.config?.details?.title
        ? this.config?.details.title
        : this.$t('pickupPoint.addressTitle')
    },
    addressTitle(): string {
      return this.address.title ?? ''
    },
    addressLine1(): string {
      return this.address.addressLine1?.toLowerCase() ?? ''
    },
    addressLine2(): string {
      return this.address.addressLine2?.toLowerCase() ?? ''
    },
    postalCode(): string {
      return this.address.postalCode
    },
    city(): string {
      return this.address.city
    },
    country(): string {
      return this.address.country
    },
  },
  methods: {
    isCurrentlyOpen(day: string, time: OpeningHoursInterval): boolean {
      const now = new Date()
      const today = now.toLocaleDateString('en', { weekday: 'long' })

      if (this.closedExceptionalDays.includes(today.toLowerCase())) {
        return false
      }
      if (today.toLowerCase() === day) {
        const startTimeParts: string[] = time.start.split(':')
        const endTimeParts: string[] = time.end.split(':')

        const startTime = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate(),
          Number(startTimeParts[0]),
          Number(startTimeParts[1]),
        )
        const endTime = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate(),
          Number(endTimeParts[0]),
          Number(endTimeParts[1]),
        )
        return now >= startTime && now <= endTime
      }
      return false
    },
    formatDate(day: string): string {
      return this.$t(`common.week.${day}`)
    },
    formatTimeRange(start: string, end: string): string {
      return `${start} - ${end}`
    },
    openMap(): void {
      const mapUrl = `${this.coordinates.latitude},${this.coordinates.longitude}`
      if (navigator.userAgent.match(/iPhone/i)) {
        window.open(`maps://maps.apple.com/?q=${mapUrl}`, '_blank')
      } else {
        window.open(`https://www.google.com/maps?q=${mapUrl}`, '_blank')
      }
    },
  },
})
